import { Component, ErrorInfo, ReactNode } from "react";
import styles from "./style.module.css";

type Props = { children: ReactNode };
type State = { hasError: boolean; error: unknown };

function normalizeStatusCode(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const e = error as Record<string, unknown>;
  const res = e.response as Record<string, unknown> | undefined;
  const nested = e.error as Record<string, unknown> | undefined;

  return (
    (typeof e.statusCode === "number" ? e.statusCode : null) ??
    (typeof e.status === "number" ? e.status : null) ??
    (typeof res?.status === "number" ? (res.status as number) : null) ??
    (typeof (res?.data as Record<string, unknown>)?.statusCode === "number"
      ? ((res!.data as Record<string, unknown>).statusCode as number)
      : null) ??
    (typeof nested?.statusCode === "number"
      ? (nested.statusCode as number)
      : null) ??
    null
  );
}

export function reportErrorToBoundary(error: unknown): void {
  window.dispatchEvent(new CustomEvent("app:error", { detail: error }));
}

const ServerErrorSVG = () => (
  <svg
    width="200"
    height="165"
    viewBox="0 0 200 165"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Left block */}
    <polygon points="12,80 55,80 55,150 12,150" fill="#D1D5DB" />
    <polygon points="12,80 26,66 69,66 55,80" fill="#E5E7EB" />
    <polygon points="55,80 69,66 69,136 55,150" fill="#9CA3AF" />
    {/* Middle block — tallest */}
    <polygon points="72,35 115,35 115,150 72,150" fill="#94A3B8" />
    <polygon points="72,35 86,21 129,21 115,35" fill="#CBD5E1" />
    <polygon points="115,35 129,21 129,136 115,150" fill="#64748B" />
    {/* Right block */}
    <polygon points="132,52 170,52 170,150 132,150" fill="#B0B9C8" />
    <polygon points="132,52 146,38 184,38 170,52" fill="#D9E2EC" />
    <polygon points="170,52 184,38 184,136 170,150" fill="#8898AA" />
  </svg>
);

const ForbiddenSVG = () => (
  <svg
    width="130"
    height="145"
    viewBox="0 0 130 145"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M65 8 L108 28 L108 76 Q108 112 65 138 Q22 112 22 76 L22 28 Z"
      fill="#E5E7EB"
      stroke="#D1D5DB"
      strokeWidth="2"
    />
    <rect x="44" y="72" width="38" height="30" rx="5" fill="#94A3B8" />
    <path
      d="M53 72 L53 60 Q53 46 65 46 Q77 46 77 60 L77 72"
      stroke="#64748B"
      strokeWidth="6"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="63" cy="83" r="5" fill="#E5E7EB" />
    <rect x="60" y="83" width="6" height="9" rx="2" fill="#E5E7EB" />
  </svg>
);

const UnknownErrorSVG = () => (
  <svg
    width="140"
    height="130"
    viewBox="0 0 140 130"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M70 12 L128 115 L12 115 Z"
      fill="#FEF3C7"
      stroke="#FCD34D"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <rect x="64" y="50" width="12" height="36" rx="6" fill="#F59E0B" />
    <circle cx="70" cy="97" r="7" fill="#F59E0B" />
  </svg>
);

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      "[ErrorBoundary] React render error:",
      error,
      info.componentStack,
    );
  }

  componentDidMount(): void {
    window.addEventListener("app:error", this.handleExternalError);
  }

  componentWillUnmount(): void {
    window.removeEventListener("app:error", this.handleExternalError);
  }

  private handleExternalError = (event: Event): void => {
    this.setState({ hasError: true, error: (event as CustomEvent).detail });
  };

  private handleGoHome = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const statusCode = normalizeStatusCode(this.state.error);

    let svg: ReactNode;
    let title: string;
    let description: ReactNode;

    if (statusCode === 403) {
      svg = <ForbiddenSVG />;
      title = "Không có quyền truy cập";
      description =
        "Bạn không có quyền xem trang này. Vui lòng liên hệ quản trị viên nếu bạn cho rằng đây là lỗi.";
    } else if (statusCode !== null && statusCode >= 500) {
      svg = <ServerErrorSVG />;
      title = "Oops, đó là lỗi của chúng tôi";
      description =
        "Chúng tôi không chắc điều gì đã xảy ra, nhưng có lỗi từ phía máy chủ. Vui lòng thử lại sau.";
    } else {
      svg = <UnknownErrorSVG />;
      title = "Đã xảy ra lỗi";
      description =
        "Chúng tôi không chắc điều gì đã xảy ra, nhưng có gì đó không ổn. Vui lòng thử lại.";
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <div className={styles.illustration}>{svg}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
          <div className={styles.actions}>
            <button className={styles.btnSecondary} onClick={this.handleReload}>
              Tải lại trang
            </button>
            <button className={styles.btnPrimary} onClick={this.handleGoHome}>
              Quay lại Trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }
}
